import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/app/context/AuthContext';
import { Header } from '@/app/components/Header';
import { Footer } from '@/app/components/Footer';
import { SubscriptionPreviewModal } from '@/app/components/SubscriptionPreviewModal';
import { API_ENDPOINTS } from '@/config/api';
import { 
  User, 
  Mail, 
  Key, 
  Calendar, 
  Copy, 
  Check, 
  CreditCard,
  BarChart3,
  Package,
  AlertCircle,
  Loader2,
  Crown,
  Zap,
  ArrowUpRight,
  CircleX,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Trash2,
  ShieldAlert
} from 'lucide-react';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  profile_picture_url: string;
  email_verified: boolean;
  created_at: string;
  last_login_at: string;
  linked_providers: string[];
}

interface License {
  id: string;
  license_key: string;
  status: string;
  plan: string;
  plan_display_name?: string;
  billing_cycle: string;
  price_usd: number;
  can_self_serve_upgrade?: boolean;
  can_cancel_subscription?: boolean;
  generations_included: number;
  generations_used: number;
  overage_enabled: boolean;
  overage_used: number;
  overage_rate: number;
  created_at: string;
  cancel_at_period_end?: boolean;
  expires_at?: string | null;
}

interface UsageInfo {
  tier: string;
  plan: string;
  generations: {
    included: number;
    used: number;
    remaining: number;
    percentage: number;
  };
  overage: {
    enabled: boolean;
    rate: number;
    used: number;
    cost: number;
  };
  status: string;
  next_reset: string;
  cancel_at_period_end?: boolean;
  expires_at?: string | null;
}

interface PackageInfo {
  id: string;
  name: string;
  /** User-facing display title (use instead of name in UI). */
  title?: string | null;
  /** User-facing long description (use instead of description in UI). */
  details?: string | null;
  description?: string;
  subscription_type?: string;
  status?: string;
  price_usd?: number;
}

interface PackagesData {
  included: PackageInfo[];
  purchased: PackageInfo[];
  available: PackageInfo[];
  all_active: string[];
}

type TabType = 'account' | 'subscription' | 'usage' | 'billing';

export function AccountPage() {
  const navigate = useNavigate();
  const { user, license, isAuthenticated, isLoading: authLoading, refreshLicense, updateLicense, deleteAccount } = useAuth();
  
  const [activeTab, setActiveTab] = useState<TabType>('account');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [licenseDetails, setLicenseDetails] = useState<License | null>(null);
  const [usageInfo, setUsageInfo] = useState<UsageInfo | null>(null);
  const [packages, setPackages] = useState<PackagesData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [packageActionLoading, setPackageActionLoading] = useState<string | null>(null);
  const [removePackageModal, setRemovePackageModal] = useState<{ id: string; name: string } | null>(null);
  const [billingDetailsExpanded, setBillingDetailsExpanded] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [packageAddPreview, setPackageAddPreview] = useState<{
    isOpen: boolean;
    isLoading: boolean;
    error: string | null;
    preview: {
      type: 'package_add';
      package_id: string;
      package_name?: string;
      package_title?: string;
      package_description?: string;
      package_details?: string;
      monthly_price: number;
      proration_amount: number;
      amount_due_now: number;
      next_billing_date: string;
      currency: string;
      stripe_price_id: string;
    } | null;
    packageId: string | null;
  }>({ isOpen: false, isLoading: false, error: null, preview: null, packageId: null });

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'account', label: 'Account Details', icon: <User className="w-4 h-4" /> },
    { id: 'subscription', label: 'Subscription', icon: <Crown className="w-4 h-4" /> },
    { id: 'usage', label: 'Usage', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'billing', label: 'Billing & Payments', icon: <CreditCard className="w-4 h-4" /> },
  ];

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Refresh license from profile on load so Account shows current plan after checkout
  useEffect(() => {
    if (user && isAuthenticated) {
      refreshLicense();
    }
  }, [user?.id, isAuthenticated, refreshLicense]);

  useEffect(() => {
    if (user) {
      fetchAccountData();
    }
  }, [user?.id]);

  const fetchAccountData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const profileRes = await fetch(API_ENDPOINTS.USER_PROFILE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      });
      const profileData = await profileRes.json().catch(() => null);

      if (!profileRes.ok) {
        console.error('Profile API error:', profileRes.status, profileData?.error, profileData?.license_error ?? profileData?.license_code ?? profileData?.license_details ?? profileData);
      }

      if (profileRes.ok && profileData?.success) {
        setProfile(profileData.user);
        setLicenseDetails(profileData.license ?? null);
        if (profileData.license) {
          updateLicense(profileData.license.license_key, profileData.license.plan || 'free');
        }
      }

      const licenseKey = profileData?.license?.license_key ?? license?.license_key;
      if (licenseKey) {
        const [usageRes, packagesRes] = await Promise.all([
          fetch(API_ENDPOINTS.USAGE_INFO, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ licenseKey }),
          }),
          fetch(API_ENDPOINTS.PACKAGES_LIST, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ licenseKey }),
          }),
        ]);
        if (usageRes.ok) {
          const usageData = await usageRes.json();
          if (usageData?.valid) setUsageInfo(usageData);
        }
        if (packagesRes.ok) {
          const packagesData = await packagesRes.json();
          if (packagesData?.valid) setPackages(packagesData.packages);
        }
      }
    } catch (err) {
      console.error('Error fetching account data:', err);
      setError('Failed to load account data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const displayLicense = license ?? licenseDetails;
  const planDisplayName = licenseDetails?.plan_display_name ?? displayLicense?.plan ?? 'Free';

  const copyLicenseKey = async () => {
    if (displayLicense?.license_key) {
      await navigator.clipboard.writeText(displayLicense.license_key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal':
        return 'text-green-600 dark:text-green-400';
      case 'warning':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'exceeded':
      case 'overage':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getProgressBarColor = (percentage: number) => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPlanFeatures = () => {
    const features: string[] = [];
    
    if (usageInfo) {
      const included = usageInfo.generations.included;
      if (included === 999999999999) {
        features.push('Unlimited generations');
      } else {
        features.push(`${included} generations per month`);
      }
    }
    
    if (packages) {
      const includedCount = packages.included.length;
      if (includedCount > 0) {
        features.push(`${includedCount} content package${includedCount > 1 ? 's' : ''} included`);
      }
    }
    
    if (usageInfo?.overage.enabled) {
      features.push(`Overage available at $${usageInfo.overage.rate.toFixed(2)}/generation`);
    }
    
    const planLower = (displayLicense?.plan || 'free').toLowerCase();
    if (planLower === 'pro' || planLower === 'enterprise') {
      features.push('Priority support');
    } else {
      features.push('Email support');
    }
    
    if (planLower === 'enterprise') {
      features.push('Dedicated support');
      features.push('Custom integrations');
      features.push('Team management');
    }
    
    return features;
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-[#0A1628] dark:to-[#0A1628]">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading account data...</span>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const planLower = (displayLicense?.plan || 'free').toLowerCase();
  const isCancelScheduled = (d: { cancel_at_period_end?: boolean; expires_at?: string | null } | null) =>
    Boolean(d && (d.cancel_at_period_end === true || String(d.cancel_at_period_end || '').toLowerCase() === 'true') && d.expires_at);
  const cancellationScheduled = isCancelScheduled(usageInfo) || isCancelScheduled(licenseDetails);
  const expirationDate = usageInfo?.expires_at ?? licenseDetails?.expires_at ?? null;
  const canCancelPlan = licenseDetails?.can_cancel_subscription === true && displayLicense?.license_key && !cancellationScheduled;
  const hasActiveSubscription = planLower !== 'free' && !cancellationScheduled && licenseDetails?.can_cancel_subscription === true;

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await deleteAccount('DELETE');
      navigate('/');
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete account');
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderAccountTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-shrink-0">
          {user?.profile_picture_url ? (
            <img
              src={user.profile_picture_url}
              alt={user.name}
              className="w-24 h-24 rounded-full border-4 border-[#1E3A8A]/20"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-[#1E3A8A] flex items-center justify-center">
              <User size={40} className="text-white" />
            </div>
          )}
        </div>

        <div className="flex-1 space-y-4">
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Name</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">{user?.name}</p>
          </div>

          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-400" />
            <span className="text-gray-700 dark:text-gray-300">{user?.email}</span>
            {profile?.email_verified && (
              <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                Verified
              </span>
            )}
          </div>

          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400 block mb-2">Linked Accounts</label>
            <div className="flex gap-3">
              {profile?.linked_providers?.includes('google') && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="text-sm text-gray-700 dark:text-gray-300">Google</span>
                </div>
              )}
              {profile?.linked_providers?.includes('linkedin') && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#0A66C2">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  <span className="text-sm text-gray-700 dark:text-gray-300">LinkedIn</span>
                </div>
              )}
            </div>
          </div>

          {profile?.created_at && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Calendar className="w-4 h-4" />
              <span>Member since {formatDate(profile.created_at)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Key className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500 dark:text-gray-400">License Key</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono text-gray-700 dark:text-gray-300">
              {displayLicense?.license_key}
            </code>
            <button
              onClick={copyLicenseKey}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Copy license key"
            >
              {copiedKey ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Current Plan</h3>
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#1E3A8A]/5 to-[#60A5FA]/5 dark:from-[#1E3A8A]/20 dark:to-[#60A5FA]/20 border border-[#1E3A8A]/20 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#1E3A8A] flex items-center justify-center">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{planDisplayName} Plan</p>
              {licenseDetails?.billing_cycle && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {licenseDetails.billing_cycle === 'monthly' ? 'Monthly' : 'Annual'} billing
                  {licenseDetails.price_usd > 0 && ` - $${licenseDetails.price_usd.toFixed(2)}/mo`}
                </p>
              )}
              {cancellationScheduled && expirationDate && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-0.5">Valid through {formatDate(expirationDate)}</p>
              )}
            </div>
          </div>
          {cancellationScheduled ? (
            <button
              type="button"
              onClick={handleReactivate}
              disabled={reactivateLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {reactivateLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              Undo Cancellation
            </button>
          ) : (
            <Link
              to="/pricing"
              className="px-4 py-2 bg-[#1E3A8A] text-white rounded-lg hover:bg-[#1E3A8A]/90 transition-colors flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Upgrade Plan
            </Link>
          )}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="pt-6 border-t border-red-200 dark:border-red-800/50">
        <h3 className="text-sm font-medium text-red-600 dark:text-red-400 mb-4 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4" />
          Danger Zone
        </h3>
        <div className="p-4 border border-red-200 dark:border-red-800/50 rounded-xl bg-red-50/50 dark:bg-red-900/10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Delete Account</p>
              {hasActiveSubscription ? (
                <div className="mt-1 space-y-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Your active subscription will be cancelled immediately with no refund.
                    All your data will be permanently deleted.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">
                    Alternatively, you can{' '}
                    <button
                      type="button"
                      onClick={() => setActiveTab('subscription')}
                      className="text-[#1E3A8A] dark:text-[#60A5FA] underline hover:no-underline font-medium"
                    >
                      cancel your subscription first
                    </button>
                    {' '}and continue using the service until the end of your billing period, then delete your account.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  This will permanently delete your account and all associated data. This action cannot be undone.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setDeleteModalOpen(true); setDeleteConfirmText(''); setDeleteError(null); }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors font-medium text-sm whitespace-nowrap flex-shrink-0"
            >
              <Trash2 className="w-4 h-4" />
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const handleCancelPlan = async () => {
    if (!displayLicense?.license_key) return;
    setCancelLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.SUBSCRIBE_CANCEL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseKey: displayLicense.license_key }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to cancel plan');
        return;
      }
      setCancelModalOpen(false);
      setError(null);
      await fetchAccountData();
      refreshLicense();
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!displayLicense?.license_key) return;
    setReactivateLoading(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.SUBSCRIBE_REACTIVATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseKey: displayLicense.license_key }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to undo cancellation');
        return;
      }
      await fetchAccountData();
      refreshLicense();
    } finally {
      setReactivateLoading(false);
    }
  };

  const handleAddPackage = async (packageId: string) => {
    if (!displayLicense?.license_key) return;
    
    // Open preview modal and fetch proration preview
    setPackageAddPreview({
      isOpen: true,
      isLoading: true,
      error: null,
      preview: null,
      packageId,
    });

    try {
      const previewRes = await fetch(API_ENDPOINTS.PACKAGES_PREVIEW_ADD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          licenseKey: displayLicense.license_key,
          packageId,
        }),
      });
      const previewData = await previewRes.json();
      
      if (!previewRes.ok || !previewData.success) {
        setPackageAddPreview(prev => ({
          ...prev,
          isLoading: false,
          error: previewData.error || 'Failed to calculate proration preview',
        }));
        return;
      }

      setPackageAddPreview(prev => ({
        ...prev,
        isLoading: false,
        preview: { type: 'package_add', ...previewData.preview },
      }));
    } catch (previewError) {
      setPackageAddPreview(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load preview. Please try again.',
      }));
    }
  };

  const handleConfirmAddPackage = async () => {
    if (!packageAddPreview.preview || !displayLicense?.license_key || !packageAddPreview.packageId) return;

    try {
      const res = await fetch(API_ENDPOINTS.PACKAGES_ADD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          licenseKey: displayLicense.license_key,
          packageId: packageAddPreview.packageId,
        }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setPackageAddPreview(prev => ({
          ...prev,
          error: data.error || 'Failed to add package',
        }));
        return;
      }

      // Success - close modal and refresh
      setPackageAddPreview({ isOpen: false, isLoading: false, error: null, preview: null, packageId: null });
      await fetchAccountData();
      refreshLicense();
    } catch (error) {
      setPackageAddPreview(prev => ({
        ...prev,
        error: 'Failed to add package. Please try again.',
      }));
    }
  };

  const handleRemovePackage = async (packageId: string) => {
    if (!displayLicense?.license_key) return;
    setPackageActionLoading(packageId);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.PACKAGES_REMOVE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          licenseKey: displayLicense.license_key,
          packageId,
          immediate: false,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to remove package');
        return;
      }
      setRemovePackageModal(null);
      await fetchAccountData();
    } finally {
      setPackageActionLoading(null);
    }
  };

  const renderSubscriptionTab = () => (
    <div className="space-y-6">
      {cancellationScheduled && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="font-medium text-amber-800 dark:text-amber-200">Cancellation scheduled</p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">
              Your plan will cancel on {expirationDate ? formatDate(expirationDate) : 'the end of your billing period'}. You will keep access until then.
            </p>
          </div>
          <button
            type="button"
            onClick={handleReactivate}
            disabled={reactivateLoading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {reactivateLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Undo cancellation
          </button>
        </div>
      )}

      <div className="p-6 bg-gradient-to-r from-[#1E3A8A]/10 to-[#60A5FA]/10 dark:from-[#1E3A8A]/30 dark:to-[#60A5FA]/30 border border-[#1E3A8A]/20 rounded-xl">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-[#1E3A8A] flex items-center justify-center">
              <Crown className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{planDisplayName} Plan</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Your current subscription</p>
              {cancellationScheduled && expirationDate && (
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">Valid through {formatDate(expirationDate)}</p>
              )}
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${cancellationScheduled ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'}`}>
            {cancellationScheduled ? 'Cancelling' : 'Active'}
          </span>
        </div>
        
        <div className="space-y-2 mb-6">
          {getPlanFeatures().map((feature, index) => (
            <div key={index} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <Check className="w-4 h-4 text-green-500" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {licenseDetails?.can_self_serve_upgrade !== false && !cancellationScheduled && (
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E3A8A] text-white rounded-lg hover:bg-[#1E3A8A]/90 transition-colors font-medium"
            >
              <Zap className="w-5 h-5" />
              Upgrade Plan
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          )}
          {canCancelPlan && (
            <button
              type="button"
              onClick={() => setCancelModalOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors font-medium"
            >
              <CircleX className="w-5 h-5" />
              Cancel Plan
            </button>
          )}
        </div>
      </div>

      {cancelModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => !cancelLoading && setCancelModalOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Cancel plan?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Your plan will be cancelled at the end of your current billing period. You will keep access until then. No further charges will be made.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => !cancelLoading && setCancelModalOpen(false)}
                disabled={cancelLoading}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50"
              >
                Keep plan
              </button>
              <button
                type="button"
                onClick={handleCancelPlan}
                disabled={cancelLoading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium disabled:opacity-50"
              >
                {cancelLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Cancel at period end
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-[#1E3A8A]" />
          Content Packages
        </h3>

        {packages ? (
          <div className="space-y-4">
            {(packages.included.length > 0 || packages.purchased.length > 0) && (
              <div>
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Active Packages</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {packages.included.map((pkg) => (
                    <div
                      key={pkg.id}
                      className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-green-800 dark:text-green-300">{pkg.title ?? pkg.name}</span>
                        <span className="text-xs px-2 py-0.5 bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200 rounded-full">
                          Included
                        </span>
                      </div>
                      {(pkg.details ?? pkg.description) && (
                        <p className="text-sm text-green-700 dark:text-green-400">{pkg.details ?? pkg.description}</p>
                      )}
                    </div>
                  ))}
                  {packages.purchased.map((pkg) => (
                    <div
                      key={pkg.id}
                      className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-blue-800 dark:text-blue-300">{pkg.title ?? pkg.name}</span>
                        <div className="flex items-center gap-2">
                          {pkg.price_usd && (
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                              ${pkg.price_usd.toFixed(2)}/mo
                            </span>
                          )}
                          <span className="text-xs px-2 py-0.5 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full">
                            Add-on
                          </span>
                        </div>
                      </div>
                      {(pkg.details ?? pkg.description) && (
                        <p className="text-sm text-blue-700 dark:text-blue-400 mb-2">{pkg.details ?? pkg.description}</p>
                      )}
                      <button
                        type="button"
                        onClick={() => setRemovePackageModal({ id: pkg.id, displayName: pkg.title ?? pkg.name })}
                        disabled={packageActionLoading === pkg.id}
                        className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium disabled:opacity-50"
                      >
                        {packageActionLoading === pkg.id ? 'Removing...' : 'Remove package'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {packages.available.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Available for Purchase</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {packages.available.map((pkg) => (
                    <div
                      key={pkg.id}
                      className="p-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-800 dark:text-gray-200">{pkg.title ?? pkg.name}</span>
                        <span className="text-sm font-medium text-[#1E3A8A] dark:text-[#60A5FA]">
                          ${pkg.price_usd?.toFixed(2)}/mo
                        </span>
                      </div>
                      {(pkg.details ?? pkg.description) && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{pkg.details ?? pkg.description}</p>
                      )}
                      <button
                        type="button"
                        onClick={() => handleAddPackage(pkg.id)}
                        className="inline-flex items-center gap-1 text-sm text-[#1E3A8A] dark:text-[#60A5FA] hover:text-[#1E3A8A]/80 dark:hover:text-[#60A5FA]/80 font-medium"
                      >
                        <Zap className="w-3 h-3" />
                        Add package
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {packages.included.length === 0 && packages.purchased.length === 0 && packages.available.length === 0 && (
              <p className="text-gray-500 dark:text-gray-400">No packages available.</p>
            )}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">Unable to load packages data.</p>
        )}
      </div>
    </div>
  );

  const renderUsageTab = () => (
    <div className="space-y-6">
      {usageInfo ? (
        <>
          <div className="p-6 bg-white dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Generations Used</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {usageInfo.generations.used}
                </span>
                <span className="text-lg text-gray-500 dark:text-gray-400">
                  / {usageInfo.generations.included === 999999999999 ? 'Unlimited' : usageInfo.generations.included}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                <div
                  className={`h-4 rounded-full transition-all ${getProgressBarColor(usageInfo.generations.percentage)}`}
                  style={{ width: `${Math.min(usageInfo.generations.percentage, 100)}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  {usageInfo.generations.remaining === 999999999999 ? 'Unlimited' : usageInfo.generations.remaining} remaining
                </span>
                <span className={`font-medium ${getStatusColor(usageInfo.status)}`}>
                  {usageInfo.status === 'normal' && 'Good'}
                  {usageInfo.status === 'warning' && 'Approaching limit'}
                  {usageInfo.status === 'exceeded' && 'Limit exceeded'}
                  {usageInfo.status === 'overage' && 'Using overage'}
                </span>
              </div>
            </div>
          </div>

          {usageInfo.overage.enabled && usageInfo.overage.used > 0 && (
            <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
              <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-300 mb-4">Overage Usage</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400">Overage generations</p>
                  <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">{usageInfo.overage.used}</p>
                </div>
                <div>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400">Overage cost</p>
                  <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">${usageInfo.overage.cost.toFixed(2)}</p>
                </div>
              </div>
              <p className="mt-4 text-sm text-yellow-700 dark:text-yellow-400">
                Rate: ${usageInfo.overage.rate.toFixed(3)} per generation
              </p>
            </div>
          )}

          {usageInfo.next_reset && (
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Usage resets on</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatDate(usageInfo.next_reset)}</p>
              </div>
            </div>
          )}
        </>
      ) : (
        <p className="text-gray-500 dark:text-gray-400">Unable to load usage data.</p>
      )}
    </div>
  );

  const renderBillingTab = () => {
    // Calculate total monthly cost including packages
    const planCost = licenseDetails?.price_usd || 0;
    const packagesCost = packages?.purchased?.reduce((sum, pkg) => sum + (pkg.price_usd || 0), 0) || 0;
    const overageCost = usageInfo?.overage.cost || 0;
    const totalMonthly = planCost + packagesCost + overageCost;
    const hasPackages = packages?.purchased && packages.purchased.length > 0;
    const hasOverages = usageInfo?.overage.enabled && overageCost > 0;

    return (
      <div className="space-y-6">
        <div className="p-6 bg-white dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Billing Summary</h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Current Plan</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{planDisplayName}</p>
              {cancellationScheduled && expirationDate && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-0.5">Valid through {formatDate(expirationDate)}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Estimated Monthly Total</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ${totalMonthly.toFixed(2)}
              </p>
              {(hasPackages || hasOverages) && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Includes {hasPackages ? 'packages' : ''}{hasPackages && hasOverages ? ' & ' : ''}{hasOverages ? 'overages' : ''}
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setBillingDetailsExpanded(!billingDetailsExpanded)}
              className="flex items-center gap-2 text-sm font-medium text-[#1E3A8A] dark:text-[#60A5FA] hover:underline"
            >
              {billingDetailsExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {billingDetailsExpanded ? 'Hide' : 'View'} Itemized Bill
            </button>

            {billingDetailsExpanded && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-[#1E3A8A] dark:text-[#60A5FA]" />
                    <span className="text-gray-700 dark:text-gray-300">{planDisplayName} Plan</span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">${planCost.toFixed(2)}</span>
                </div>

                {packages?.purchased?.map((pkg) => (
                  <div key={pkg.id} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-blue-500" />
                      <span className="text-gray-700 dark:text-gray-300">{pkg.name} Package</span>
                    </div>
                    <span className="font-medium text-gray-900 dark:text-white">${(pkg.price_usd || 0).toFixed(2)}</span>
                  </div>
                ))}

                {hasOverages && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-yellow-500" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Estimated Overages ({usageInfo?.overage.used || 0} generations @ ${usageInfo?.overage.rate?.toFixed(2)}/ea)
                      </span>
                    </div>
                    <span className="font-medium text-yellow-600 dark:text-yellow-400">+${overageCost.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex items-center justify-between pt-3 mt-2 border-t-2 border-gray-200 dark:border-gray-600">
                  <span className="font-semibold text-gray-900 dark:text-white">Estimated Total</span>
                  <span className="text-xl font-bold text-gray-900 dark:text-white">${totalMonthly.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 bg-white dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Payment History</h3>
          <div className="text-center py-8">
            <CreditCard className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">Payment history will be available soon.</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Stripe integration coming soon</p>
          </div>
        </div>

      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-[#0A1628] dark:to-[#0A1628]">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">My Account</h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <span className="text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
            <nav className="flex gap-1 -mb-px overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-[#1E3A8A] text-[#1E3A8A] dark:border-[#60A5FA] dark:text-[#60A5FA]'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            {activeTab === 'account' && renderAccountTab()}
            {activeTab === 'subscription' && renderSubscriptionTab()}
            {activeTab === 'usage' && renderUsageTab()}
            {activeTab === 'billing' && renderBillingTab()}
          </div>
        </div>
      </main>
      <Footer />

      {removePackageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => !packageActionLoading && setRemovePackageModal(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Remove {removePackageModal.displayName}?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              This package will be removed at the end of your current billing period. You will keep access until then.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => !packageActionLoading && setRemovePackageModal(null)}
                disabled={!!packageActionLoading}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50"
              >
                Keep package
              </button>
              <button
                type="button"
                onClick={() => handleRemovePackage(removePackageModal.id)}
                disabled={!!packageActionLoading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium disabled:opacity-50"
              >
                {packageActionLoading === removePackageModal.id && <Loader2 className="w-4 h-4 animate-spin" />}
                Remove package
              </button>
            </div>
          </div>
        </div>
      )}

      <SubscriptionPreviewModal
        isOpen={packageAddPreview.isOpen}
        onClose={() => setPackageAddPreview({ isOpen: false, isLoading: false, error: null, preview: null, packageId: null })}
        onConfirm={handleConfirmAddPackage}
        preview={packageAddPreview.preview}
        isLoading={packageAddPreview.isLoading}
        error={packageAddPreview.error}
        modalType="package_add"
      />

      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => !deleteLoading && setDeleteModalOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Account</h3>
            </div>
            <div className="space-y-3 mb-6">
              {hasActiveSubscription && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
                    Your active subscription will be cancelled immediately. No refund will be issued for the remaining billing period.
                  </p>
                </div>
              )}
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This action is <span className="font-semibold text-red-600 dark:text-red-400">permanent and irreversible</span>.
                All your data, including your license, usage history, and preferences, will be deleted.
              </p>
              <div>
                <label htmlFor="delete-confirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Type <span className="font-mono font-bold text-red-600 dark:text-red-400">DELETE</span> to confirm
                </label>
                <input
                  id="delete-confirm"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                />
              </div>
              {deleteError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-300">{deleteError}</p>
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => !deleteLoading && setDeleteModalOpen(false)}
                disabled={deleteLoading}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleteLoading || deleteConfirmText !== 'DELETE'}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete My Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
